// src/hooks/useAssinatura.js
// Jordan — Bigode.app: entitlement robusto (active/trialing/past_due/unpaid)
// *SEM* janela de checkout (checkoutPendingUntil) para evitar liberar antes do pagamento real

import { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  onSnapshot,
  collection,
  query,
  where,
  limit,
  getDocs,
  collectionGroup,
} from "firebase/firestore";
import { app } from "../firebase";

const ENTITLED_STATUS = new Set(["active", "trialing", "past_due", "unpaid"]);

function ceilDiffDays(laterMs, earlierMs) {
  const diff = Math.ceil((laterMs - earlierMs) / (24 * 60 * 60 * 1000));
  return diff < 0 ? 0 : diff;
}

export default function useAssinatura({ barbeariaId: barbeariaIdProp } = {}) {
  const db = getFirestore(app);

  const [state, setState] = useState({
    loading: true,
    barbeariaId: barbeariaIdProp || null,

    // campos de status/tempo
    status: null,
    trialEndsAt: null,
    entitledUntil: null,
    daysLeft: null,

    // aux
    planId: null,
    priceId: null,
    subscriptionId: null,

    // gate
    isBlocked: false,
  });

  useEffect(() => {
    const auth = getAuth(app);
    let unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState((s) => ({ ...s, loading: false, isBlocked: true }));
        return;
      }

      let barbeariaId = barbeariaIdProp || null;

      // 1) Dono da barbearia
      if (!barbeariaId) {
        const q = query(
          collection(db, "barbearia"),
          where("criadorUid", "==", user.uid),
          limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          barbeariaId = snap.docs[0].id;
        }
      }

      // 2) Colaborador/barbeiro
      if (!barbeariaId) {
        try {
          const cg = query(
            collectionGroup(db, "barbeiros"),
            where("uid", "==", user.uid),
            limit(1)
          );
          const cgSnap = await getDocs(cg);
          if (!cgSnap.empty) {
            const parent = cgSnap.docs[0].ref.parent.parent;
            if (parent) barbeariaId = parent.id;
          }
        } catch (_) {}
      }

      // Se ainda não achou
      if (!barbeariaId) {
        setState((s) => ({ ...s, loading: false, isBlocked: true }));
        return;
      }

      const assRef = doc(db, `barbearia/${barbeariaId}/assinatura/info`);
      const unsubscribeInfo = onSnapshot(
        assRef,
        (docSnap) => {
          const data = docSnap.data() || {};
          const status = String(data.status || "trial").toLowerCase();

          const trialEndsAt =
            data.trialEndsAt?.toDate?.() ??
            (data.trial_end ? new Date(Number(data.trial_end) * 1000) : null);

          const entitledUntil =
            data.entitledUntil?.toDate?.() ??
            (data.current_period_end
              ? new Date(Number(data.current_period_end) * 1000)
              : null);

          const planId = data.planId || data.plano || null;
          const priceId = data.priceId || null;
          const subscriptionId = data.subscriptionId || null;

          const nowMs = Date.now();

          // 1) Status vindo do Stripe
          const entitledByStatus = ENTITLED_STATUS.has(status);

          // 2) Trial local válido
          const entitledByLocalTrial =
            status === "trial" &&
            trialEndsAt &&
            nowMs < trialEndsAt.getTime();

          // 3) Prazo vindo do Stripe
          const entitledByDeadline =
            entitledUntil && nowMs < entitledUntil.getTime();

          // ❗ checkoutPendingUntil REMOVIDO daqui
          // Nada mais libera o usuário apenas por clicar em assinar

          const isEntitled =
            entitledByStatus || entitledByLocalTrial || entitledByDeadline;

          const deadline =
            status === "trial"
              ? trialEndsAt || entitledUntil
              : entitledUntil || trialEndsAt;

          const daysLeft =
            deadline ? ceilDiffDays(deadline.getTime(), nowMs) : null;

          setState({
            loading: false,
            barbeariaId,
            status,
            trialEndsAt,
            entitledUntil,
            daysLeft,
            planId,
            priceId,
            subscriptionId,
            isBlocked: !isEntitled,
          });
        },
        () => {
          setState((s) => ({ ...s, loading: false, isBlocked: true }));
        }
      );

      return () => unsubscribeInfo();
    });

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
    };
  }, [db, barbeariaIdProp]);

  return state;
}
